import "./form-settings.scss";
import { Controller, useForm } from "react-hook-form";
import { Button } from "@components/button";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Textbox } from "@components/textbox";
import { Textarea } from "@components/textarea";
import { Checkbox } from "@components/checkbox";
import { useMutation, useQuery } from "@apollo/client/react";
import { GET_FORM_DETAILS, GET_FORM_QUESTIONS, UPDATE_FORM_DETAILS } from "@queries/index";
import { useParams } from "react-router-dom";
import { FormLoadingOverlay } from "@components/form-loading-overlay";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormSettingSchema, type FormSettingsData } from "./form-settings-schema";
import { useToastContext } from "@contexts/toast";
import type { Question, UFormData } from "@archetypes/form";
import { EditImageDialog } from "@dialogs/edit-image-dialog";
import { LabelInfo } from "@components/label-info";
import { CropType, type FileUploaded } from "@archetypes/generic";

import {
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PanelTitle,
  PanelTitleArea,
} from "@components/panel";
import { CloseIcon } from "@components/icons";
import { UploadMediaType } from "@archetypes/app";
import { MultiSelect } from "@components/multi-select";
import { getQuestionsForRulesOrDataView, isCompositeField } from "@utils/question.util";
import { convertNullToUndefinedAndRemoveTypeName } from "@utils/convert-null-to-undefined-and-remove-typename.util";
interface FormSettingsContainerProps {
  onClose: () => void;
}

export const FormSettingsContainer: FC<FormSettingsContainerProps> = ({ onClose }) => {
  const { formId, appId } = useParams();
  const { showToast } = useToastContext();
  const [isEditImageDialogOpen, toggleEditImageDialog] = useState(false);
  const toggleEditImageDialogHandler = useCallback(() => {
    toggleEditImageDialog((prev) => !prev);
  }, []);

  const { data: questionsData, loading: isQuestionsLoading } = useQuery<{
    getQuestions: Question[];
  }>(GET_FORM_QUESTIONS, {
    variables: {
      input: {
        appId,
        formId,
        graphLookup: true,
      },
    },
    fetchPolicy: "network-only",
  });

  const { compositeFieldsAvailable, questionsMap } = useMemo(() => {
    if (questionsData?.getQuestions && formId) {
      const formQuestions = getQuestionsForRulesOrDataView({
        questions: questionsData?.getQuestions,
        currentFormId: formId,
      }).filter((question) => isCompositeField(question.type));
      const questionsMap = new Map<string, Question>();
      formQuestions.forEach((question) => {
        questionsMap.set(question.id, question);
      });
      return {
        compositeFieldsAvailable: formQuestions.map((question) => question.id),
        questionsMap,
      };
    } else {
      return {
        compositeFieldsAvailable: [] as string[],
        questionsMap: new Map() as Map<string, Question>,
      };
    }
  }, [formId, questionsData]);

  const { data: formData, loading: fetchFormLoading } = useQuery<{
    getForm: UFormData;
  }>(GET_FORM_DETAILS, {
    variables: {
      input: {
        formId,
        appId,
      },
    },
  });

  const [updateForm, { loading: updateFormLoading }] = useMutation(UPDATE_FORM_DETAILS);

  const filePath = useMemo(() => {
    if (formData) {
      return `organization/${formData?.getForm?.company?._id}/apps/${appId}/form/${formId}`;
    }
  }, [appId, formData, formId]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormSettingsData>({
    resolver: zodResolver(FormSettingSchema),
  });

  useEffect(() => {
    if (formData) {
      reset(
        convertNullToUndefinedAndRemoveTypeName({
          sendCopyOfResponse: !!formData?.getForm?.sendCopyOfResponse,
          enableCaptcha: !!formData?.getForm?.enableCaptcha,
          showQuestionNo: !!formData?.getForm?.showQuestionNo,
          formName: formData?.getForm?.formName,
          formDescription: formData?.getForm?.formDescription,
          coverImage: formData?.getForm?.coverImage,
          submitButtonText: formData?.getForm?.submitButtonText,
          resetFormAfterSubmit: !!formData?.getForm?.resetFormAfterSubmit,
          removeBranding: !!formData?.getForm?.removeBranding,
          compositeFields: formData?.getForm?.compositeFields,
        }),
      );
    }
  }, [formData, reset]);

  const coverImage = watch("coverImage");

  const onSubmitFormHandler = useCallback(
    async (data: FormSettingsData) => {
      try {
        await updateForm({
          variables: {
            input: {
              formId: formId,
              appId: appId,
              coverImage: data.coverImage ?? "",
              enableCaptcha: data.enableCaptcha,
              formDescription: data.formDescription,
              formName: data.formName,
              resetFormAfterSubmit: data.resetFormAfterSubmit,
              showQuestionNo: data.showQuestionNo,
              submitButtonText: data.submitButtonText,
              sendCopyOfResponse: data.sendCopyOfResponse,
              removeBranding: data.removeBranding,
              compositeFields: data.compositeFields,
            },
          },
        }).then(() => {
          showToast({
            title: "Success",
            content: "Form Updated Successfully",
            variant: "success",
          });
        });
      } catch (error) {
        showToast({
          title: "Error",
          content: "Error updating form",
          variant: "danger",
        });
      } finally {
        onClose();
      }
    },
    [updateForm, formId, appId, onClose, showToast],
  );

  const isFormDataLoading = fetchFormLoading || isQuestionsLoading;

  return (
    <div className="form-settings">
      <Panel className="mb-0">
        <PanelHeader>
          <PanelTitleArea>
            <PanelTitle>Form Settings</PanelTitle>
          </PanelTitleArea>
        </PanelHeader>
        <form onSubmit={handleSubmit(onSubmitFormHandler)} noValidate>
          <FormLoadingOverlay isLoading={isFormDataLoading || updateFormLoading}>
            <PanelBody>
              <Controller
                name="formName"
                control={control}
                render={({ field }) => (
                  <Textbox
                    type="text"
                    label="Form name"
                    placeholder="Enter form name"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    containerClassName="input mb-20"
                    errorMessage={errors.formName ? errors.formName.message : ""}
                  />
                )}
              />

              <Controller
                name="formDescription"
                control={control}
                render={({ field }) => (
                  <Textarea
                    label="Form description"
                    placeholder="Enter form description"
                    rows={5}
                    cols={45}
                    maxLength={500}
                    value={field.value}
                    onChange={field.onChange}
                    containerClassName="input"
                  />
                )}
              />

              <Controller
                name="compositeFields"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    label="Composite Fields"
                    getKey={(questionId: string) => questionId}
                    options={compositeFieldsAvailable}
                    renderOption={(questionId: string) => {
                      return <>{questionsMap.get(questionId)?.content?.label ?? ""}</>;
                    }}
                    renderValue={(questionId?: string) => {
                      return questionsMap.get(questionId ?? "")?.content?.label;
                    }}
                    placeholder="Select composite fields"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="showQuestionNo"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    type="switch"
                    checked={field.value}
                    onChange={field.onChange}
                    label="Show Question Number"
                    containerClassName="w-100"
                    labelClass="w-100 justify-between"
                  />
                )}
              />

              <Controller
                name="resetFormAfterSubmit"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    type="switch"
                    checked={field.value}
                    onChange={field.onChange}
                    label="Reset form after submission"
                    containerClassName="w-100"
                    labelClass="w-100 justify-between"
                  />
                )}
              />

              <Controller
                name="enableCaptcha"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    type="switch"
                    checked={field.value}
                    onChange={field.onChange}
                    label="Enable Form Captcha"
                    containerClassName="w-100"
                    labelClass="w-100 justify-between"
                  />
                )}
              />

              <Controller
                name="sendCopyOfResponse"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    type="switch"
                    checked={field.value}
                    onChange={field.onChange}
                    label="Send Copy Of Response"
                    containerClassName="w-100"
                    labelClass="w-100 justify-between"
                  />
                )}
              />

              <Controller
                name="removeBranding"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    type="switch"
                    checked={field.value}
                    onChange={field.onChange}
                    label="Remove Unifylist Branding"
                    containerClassName="w-100"
                    labelClass="w-100 justify-between"
                  />
                )}
              />

              <div className="form-row pt-10 mb-20">
                <Controller
                  name="submitButtonText"
                  control={control}
                  render={({ field }) => (
                    <Textbox
                      type="text"
                      label="Submit Button Text"
                      placeholder="Button Text"
                      value={field.value}
                      onChange={field.onChange}
                      containerClassName="input d-flex"
                      wrapperClass="w-100"
                      errorMessage={errors.submitButtonText ? errors.submitButtonText.message : ""}
                    />
                  )}
                />

                <Controller
                  name="coverImage"
                  control={control}
                  render={({ field }) => (
                    <>
                      <div className="cover-image w-100 d-flex">
                        <LabelInfo label="Choose Cover Image: " />
                        <Button onClick={toggleEditImageDialogHandler} variant="default-bordered">
                          Upload
                        </Button>
                      </div>
                      {coverImage && (
                        <div className="image-preview-container">
                          <img src={coverImage} alt="coverImage" decoding="async" loading="eager" />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => {
                              setValue("coverImage", undefined);
                            }}
                          >
                            <CloseIcon color="#333" size={12} />
                          </button>
                        </div>
                      )}
                      <EditImageDialog
                        fileType={UploadMediaType.Logo}
                        title="Choose your Cover Image"
                        isOpen={isEditImageDialogOpen}
                        onClose={toggleEditImageDialogHandler}
                        onFileUploaded={(response: FileUploaded) => {
                          field.onChange(response.fileUrl);
                        }}
                        filePath={filePath}
                        fileName={formData?.getForm?.formName}
                        cropType={CropType.SQUARE}
                      />
                    </>
                  )}
                />
              </div>
            </PanelBody>
          </FormLoadingOverlay>
          <PanelFooter position="sticky">
            <Button
              onClick={onClose}
              variant="cancel"
              className="mr-10"
              disabled={updateFormLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={updateFormLoading}
              disabled={isFormDataLoading || updateFormLoading}
            >
              Save
            </Button>
          </PanelFooter>
        </form>
      </Panel>
    </div>
  );
};
